import angular from 'angular';
import ComponentsModule from '../components-module';
import tinyColor from 'tinycolor2';

const COMPONENT_NAME = 'CodeWorkflow';

const getClassesFor = (transition) => {
  let colorProperty = transition.properties && transition.properties.color;

  if (colorProperty && (colorProperty === 'regressive' || colorProperty === 'progressive')) {
    delete transition.properties.color;
    return [colorProperty];
  }
};

const getColorStyleFor = (transition) => {
  let colorProperty = transition.properties && transition.properties.color;

  if (colorProperty && colorProperty !== 'regressive' && colorProperty !== 'progressive') {
    let bgColor = tinyColor(colorProperty);
    let fgColor = tinyColor.mostReadable(bgColor, tinyColor(bgColor).monochromatic(20), { includeFallbackColors: true });

    delete transition.properties.color;

    return {
      'background-color': bgColor.toHexString(),
      'color': fgColor.toHexString(),
      'border-color': bgColor.darken(20).toHexString()
    };
  }
};

angular.module (ComponentsModule)
  .factory (COMPONENT_NAME, function ($q, $uibModal, AuthorizationService, WorkflowService) {
    const confirmWorkflowTransition = (transition) => {
      const message = transition.description || '';
      const title = transition.label || transition.name || 'Workflow transition';

      const modal = $uibModal.open({
        animation: false,
        backdrop: 'static',
        keyboard: false,
        component: 'userCommentDialog',

        resolve: {
          confirmText: ($translate) => $translate('content-editor.confirm.transition.confirm.label'),
          cancelText: ($translate) => $translate('content-editor.confirm.transition.cancel.label'),
          message: ($translate) => $translate(message).catch(() => message),
          title: ($translate) => $translate(title).catch(() => title)
        }
      });

      return modal.result;
    };

    return {
      getContextualWorkflow: ({ user, metadata, content }) => {
        const contentType = atex.aceui.ObjectUtils.getByPath (content, 'system/contentType');
        const currentVersion = atex.aceui.ObjectUtils.getByPath (content, 'system/version');
        const securityContexts = atex.aceui.ObjectUtils.getByPath (metadata, 'aspects/aceSecurityContext/owners') || [];
        const workflows = atex.aceui.ObjectUtils.getByPath (metadata, 'aspects/aceWorkflowState/workflows') || [];
        const inProgress = workflows.find ((instance) => instance.status === 'IN_PROGRESS');
        const workflowId = inProgress && inProgress.workflowId;

        if (!(contentType || workflowId)) {
          return $q.resolve({});
        }

        let workflowRequest = workflowId
          ? WorkflowService.getWorkflow({ workflowId })
          : WorkflowService.getWorkflowFor({ contentType })
            .catch(error => {
            // No workflow found is an expected error, actually
              if (typeof error.type === 'undefined' || error.type !== atex.aceui.error.types.NOT_FOUND) {
                console.warn('Error while reading workflow definition.', error);

                return $q.reject(atex.aceui.error.Error({
                  origin: 'CODE workflow service',
                  type: atex.aceui.error.types.UNKNOWN,
                  severity: atex.aceui.error.severities.CRITICAL,
                  cause: 'Error reading workflow definition!',
                  rootCause: error.cause
                }));
              }
            });

        return workflowRequest
          .then((workflowDefinition) => {
            if (workflowDefinition) {
              return AuthorizationService.getAuthorizationInfo()
                .then((authInfo) => {
                  let workflow = {
                    definition: workflowDefinition,
                    name: workflowDefinition.name,
                    label: workflowDefinition.label,
                    description: workflowDefinition.description
                  };

                  if (inProgress) {
                    let currentEvent = inProgress.events[inProgress.events.length - 1];
                    let currentState = workflowDefinition.states.find((state) => state.name === currentEvent.state);

                    if (!currentState) {
                      workflow.state = {
                        name: currentEvent.state,
                        label: currentEvent.state,
                        description: currentEvent.description,
                        comment: currentEvent.comment,
                        submitter: currentEvent.principal,
                        isEditableBy: false,

                        invalid: true
                      };

                      workflow.transitions = [];
                    } else {
                      workflow.state = {
                        name: currentState.name,
                        label: currentState.label,
                        description: currentState.description,
                        comment: currentEvent.comment,
                        submitter: currentEvent.principal,
                        isEditableBy: WorkflowService.isEditableBy(currentState, user, authInfo, securityContexts)
                      };

                      workflow.transitions = currentState.transitions
                        .filter(transition => WorkflowService.isAllowed(transition, user, authInfo, securityContexts))
                        .map((transition) => {
                          let classes = getClassesFor(transition);
                          let colorStyle = getColorStyleFor(transition);

                          return {
                            name: transition.name,
                            label: transition.label,
                            description: transition.description,
                            targetState: transition.targetState,

                            classes,
                            colorStyle
                          };
                        });
                    }
                  } else {
                    let latest = workflows[workflows.length - 1];
                    let latestEvent = latest && latest.events[latest.events.length - 1];

                    if (!latestEvent || latestEvent.versionId !== currentVersion) {
                      workflow.transitions = workflowDefinition.transitions
                        .filter(transition => WorkflowService.isAllowed(transition, user, authInfo, securityContexts))
                        .map((transition) => {
                          let classes = getClassesFor(transition);
                          let colorStyle = getColorStyleFor(transition);

                          return {
                            name: transition.name,
                            label: transition.label,
                            description: transition.description,
                            targetState: transition.targetState,

                            classes,
                            colorStyle
                          };
                        });
                    }
                  }

                  return workflow;
                });
            }
          });
      },

      transition: (transition, { workflow, content, currentRevision }) => {
        return confirmWorkflowTransition (transition)
          .then((comment) => {
            let opts = {
              workflowId: workflow.name,
              target: transition.targetState,
              alias: content.system.id,
              revision: currentRevision,
              comment
            };

            return (workflow.state ? WorkflowService.transition : WorkflowService.initialize)(opts);
          }, (reason) => {
            throw atex.aceui.error.Error({
              cause: 'Workflow transition confirm modal cancelled',
              origin: 'Content Developer Workflow - Confirm transition',
              rootCause: reason,
              severity: atex.aceui.error.severities.MINIMAL,
              type: atex.aceui.error.types.OPERATION_CANCELLED
            });
          });
      }
    };
  });

export default COMPONENT_NAME;
